import { useEffect } from "react";

const ABS_URL = process.env.REACT_APP_SITE_URL;

/**
 * Lightweight per-page SEO hook (no react-helmet dependency).
 * Sets <title>, meta description, canonical, OpenGraph + Twitter tags,
 * and optionally injects a JSON-LD structured-data <script> by ID.
 */
export function useSeo({
  title,
  description,
  path = "/",
  canonical = null,
  image = "/assets/og.jpg",
  type = "website",
  jsonLd = null,
  jsonLdId = "page-jsonld",
  noindex = false,
  alternates = null,
} = {}) {
  useEffect(() => {
    const brand = "Asia Sourcing";
    const fullTitle = title
      ? (title.length > 55 || title.includes(brand)
          ? title
          : `${title} | ${brand}`)
      : `${brand} — India Sourcing & Inspection`;

    document.title = fullTitle;

    const url = canonical || (ABS_URL + path);
    const desc =
      description ||
      "India sourcing & QC inspection from Moradabad — lighting, furniture, décor, textile, brass, fashion accessories. AQL, BSCI, FSC, GOTS.";
    const absImage = image.startsWith("http") ? image : ABS_URL + image;

    setMeta("name", "description", desc);
    setMeta(
      "name",
      "robots",
      noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"
    );
    setLink("canonical", url);

    // hreflang alternates — reciprocal cluster across localized versions.
    // Clear existing hreflang alternates before rewriting so route
    // changes don't leave stale tags in the head.
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());
    if (Array.isArray(alternates)) {
      for (const alt of alternates) {
        if (!alt || !alt.hreflang || !alt.href) continue;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", alt.hreflang);
        link.setAttribute("href", alt.href);
        document.head.appendChild(link);
      }
    }

    // OpenGraph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", absImage);
    setMeta("property", "og:site_name", "Asia Sourcing");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", absImage);

    // JSON-LD
    if (jsonLd) {
      const existing = document.getElementById(jsonLdId);
      if (existing) existing.remove();
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = jsonLdId;
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    } else {
      const existing = document.getElementById(jsonLdId);
      if (existing) existing.remove();
    }

    // Cleanup on unmount / route change — remove OUR script tag so it does
    // not leak onto the next page's <head>. Without this, briefly-mounted
    // components (e.g. during hydration ordering) leave stale JSON-LD
    // that duplicates FAQPage schemas and pollutes AI-search parsing.
    return () => {
      const stale = document.getElementById(jsonLdId);
      if (stale) stale.remove();
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
    };
  }, [title, description, path, canonical, image, type, JSON.stringify(jsonLd), jsonLdId, noindex, JSON.stringify(alternates)]);
}

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export const SITE_URL = ABS_URL;
